// Palette files contain colors only. Mode assignments stay in browser settings.
export const THEME_STORAGE_KEY = "chan_fe_themes_v1";
export const BASE_COLORS = Object.freeze({
  "bg": "#eef2ff",
  "bg-hover": "#c8d0e8",
  "bg-elev": "#ffffff",
  "fg": "#000000",
  "fg-dim": "#555555",
  "fg-faint": "#888888",
  "toolbar": "#34345c",
  "link": "#34345c",
  "link-visited": "#800080",
  "border": "#b7c5d9",
  "border-soft": "#d0d6e6",
  "accent": "#0f4d92",
  "red": "#d60000",
  "on-accent": "#ffffff",
  "post-bg": "#d6daf0",
  "heading": "#0f4d92",
  "title": "#af0a0f",
  "accent-hover": "#0a3a72",
  "red-dark": "#bb0000",
  "red-hover": "#ff2a2a",
  "name": "#117743",
  "subject": "#0f0c5d",
  "quote": "#789922",
  "success": "#1a7f37",
  "info": "#3f77cc",
  "warning": "#9a7200",
  "dm-bg": "#dbf0d6",
  "dm-border": "#aecfa8",
  "dm-heading": "#4a7a44",
  "dm-toolbar": "#cfe8c9",
  "poll": "#c5cdea",
  "poll-leading": "#a9b6e4",
  "highlight": "#d6bad0",
  "inline-bg": "#dbe2f8",
  "button-hover": "#c0c8e0",
  "subtle-bg": "#f4f6ff",
  "unread-bg": "#fff1f1"
});
export const BUILTIN_THEMES = Object.freeze({
  blue: { name: "Classic blue", colors: { ...BASE_COLORS } },
  orange: { name: "Classic orange", colors: { ...BASE_COLORS, ...{"bg": "#ffffee", "bg-hover": "#f0c0b0", "bg-elev": "#ffffee", "fg": "#800000", "fg-dim": "#800000", "fg-faint": "#9b5b50", "toolbar": "#800000", "link": "#000080", "border": "#d9bfb7", "border-soft": "#e8d2c8", "accent": "#cc1105", "heading": "#cc1105", "title": "#cc1105", "post-bg": "#f0e0d6", "inline-bg": "#f0e0d6", "highlight": "#f0c0b0", "subtle-bg": "#fff8e8", "button-hover": "#f0c0b0", "poll": "#edd2be", "poll-leading": "#e4bfa5"} } },
  dark: { name: "Dark", colors: {
  "bg": "#181a20",
  "bg-hover": "#343a49",
  "bg-elev": "#242833",
  "fg": "#e1e4ed",
  "fg-dim": "#b2b9c9",
  "fg-faint": "#949db1",
  "toolbar": "#c2ccef",
  "link": "#96baff",
  "link-visited": "#cba8ef",
  "border": "#4b5367",
  "border-soft": "#363e50",
  "accent": "#385d96",
  "red": "#ff8585",
  "on-accent": "#ffffff",
  "post-bg": "#242833",
  "heading": "#aac9ff",
  "title": "#ffa0a0",
  "accent-hover": "#476fac",
  "red-dark": "#ed727e",
  "red-hover": "#ffb1b1",
  "name": "#84cda4",
  "subject": "#c2bbff",
  "quote": "#afc979",
  "success": "#84cf91",
  "info": "#91b9fa",
  "warning": "#e5c06e",
  "dm-bg": "#23382d",
  "dm-border": "#4a7055",
  "dm-heading": "#a5d7a0",
  "dm-toolbar": "#2e4837",
  "poll": "#364464",
  "poll-leading": "#495e88",
  "highlight": "#493446",
  "inline-bg": "#2b3140",
  "button-hover": "#3c455b",
  "subtle-bg": "#20242e",
  "unread-bg": "#402b33"
} },
});
const copy = value => JSON.parse(JSON.stringify(value));
export function validateTheme(value) {
  if (!value || value.format !== "chan-fe-palette" || value.version !== 1) throw new Error("Expected a chan-fe palette JSON file (version 1).");
  if (typeof value.name !== "string" || !value.name.trim() || value.name.length > 80) throw new Error("Theme names must contain 1–80 characters.");
  if (!value.colors || typeof value.colors !== "object" || Array.isArray(value.colors)) throw new Error("Missing palette colors.");
  const colors = {};
  for (const key of Object.keys(BASE_COLORS)) {
    if (!/^#[0-9a-f]{6}$/i.test(value.colors[key] || "")) throw new Error(`Invalid or missing color: ${key}. Use six-digit hex colors.`);
    colors[key] = value.colors[key].toLowerCase();
  }
  if (Object.keys(value.colors).some(key => !Object.hasOwn(BASE_COLORS, key))) throw new Error("This file contains unknown palette colors.");
  return { name: value.name.trim(), colors };
}
export function themeFile(theme) {
  return { format: "chan-fe-palette", version: 1, ...copy(theme) };
}
export function defaultThemes() {
  return { shared: false, sfw: copy(BUILTIN_THEMES.blue), nsfw: copy(BUILTIN_THEMES.orange), both: copy(BUILTIN_THEMES.blue), library: [] };
}
export function readThemes(storage) {
  try {
    const value = JSON.parse(storage.getItem(THEME_STORAGE_KEY));
    if (!value || typeof value.shared !== "boolean" || !Array.isArray(value.library)) return defaultThemes();
    return { shared: value.shared, ...Object.fromEntries(["sfw", "nsfw", "both"].map(key => [key, validateTheme(themeFile(value[key]))])), library: value.library.map(theme => validateTheme(themeFile(theme))) };
  } catch { return defaultThemes(); }
}
export function selectedTheme(settings, nsfw) {
  return settings[settings.shared ? "both" : nsfw ? "nsfw" : "sfw"];
}
export function setShared(settings, shared, mode) {
  if (shared && !settings.shared) settings.both = copy(settings[mode]);
  settings.shared = shared;
}
export function applyPalette(element, theme) {
  for (const key of Object.keys(BASE_COLORS)) element.style.setProperty(`--${key}`, theme.colors[key]);
  const rgb = theme.colors.bg.slice(1).match(/../g).map(v => parseInt(v, 16));
  element.style.colorScheme = (rgb[0] * .2126 + rgb[1] * .7152 + rgb[2] * .0722) < 128 ? "dark" : "light";
}
function savedThemes() {
  try { return readThemes(localStorage); } catch { return defaultThemes(); }
}
export function applySavedTheme(nsfw) {
  applyPalette(document.documentElement, selectedTheme(savedThemes(), nsfw));
}

const COLOR_GROUPS = {
  Surfaces: { bg: "Page", "post-bg": "Posts and headers", "bg-elev": "Inputs and controls", "bg-hover": "Hover background", "inline-bg": "Inline replies", "subtle-bg": "Secondary panels", "button-hover": "Secondary hover", highlight: "Highlighted posts / own poll vote", "unread-bg": "Unread notifications" },
  Text: { fg: "Body text", "fg-dim": "Muted text", "fg-faint": "Secondary text", toolbar: "Toolbar / backlinks", link: "Links", "link-visited": "Visited links", heading: "Headings", title: "Board title", name: "Author names", subject: "Subjects / OP names", quote: "Quote text" },
  Actions: { red: "Red — quotes and actions", "red-dark": "Dark red — selected / errors", "red-hover": "Bright red — hover", accent: "Primary button", "accent-hover": "Primary button hover", "on-accent": "Primary button text", success: "Success / repost", info: "Pinned / information", warning: "Watch indicator" },
  Details: { border: "Borders", "border-soft": "Subtle borders", poll: "Poll bars", "poll-leading": "Leading poll bar", "dm-bg": "Direct message background", "dm-border": "Direct message border", "dm-heading": "Direct message text", "dm-toolbar": "Direct message header" },
};

export function createThemeEditor(panel, isNsfw) {
  let draft, mode = "sfw", previewPage = false, generation = 0;
  const get = id => panel.querySelector(`#theme-${id}`);
  const current = () => draft[draft.shared ? "both" : mode];
  const status = message => { get("status").textContent = message; };
  const restore = () => applySavedTheme(isNsfw());
  const refreshPreview = () => {
    applyPalette(get("preview"), current());
    if (previewPage) applyPalette(document.documentElement, current());
  };
  const checkFields = () => {
    get("name").setCustomValidity(get("name").value.trim() ? "" : "Enter a theme name.");
    if (!get("name").reportValidity()) return false;
    for (const input of panel.querySelectorAll('[data-hex]')) {
      if (!input.checkValidity()) {
        input.closest("details").open = true;
        input.reportValidity();
        return false;
      }
    }
    return true;
  };
  const populate = () => {
    get("shared").checked = draft.shared;
    get("modes").hidden = draft.shared;
    get("both-label").hidden = !draft.shared;
    for (const button of get("modes").querySelectorAll("button")) button.setAttribute("aria-pressed", String(button.dataset.mode === mode));
    get("name").setCustomValidity("");
    get("name").value = current().name;
    const select = get("preset");
    select.replaceChildren(new Option("Choose a theme…", ""));
    for (const [key, theme] of Object.entries(BUILTIN_THEMES)) select.add(new Option(theme.name, `builtin:${key}`));
    draft.library.forEach((theme, i) => select.add(new Option(`${theme.name} (saved)`, `saved:${i}`)));
    for (const input of panel.querySelectorAll('[data-color], [data-hex]')) {
      input.value = current().colors[input.dataset.color || input.dataset.hex];
    }
    refreshPreview();
  };
  for (const [groupName, colors] of Object.entries(COLOR_GROUPS)) {
    const group = document.createElement("details");
    group.open = groupName === "Surfaces";
    const summary = document.createElement("summary");
    summary.textContent = groupName;
    group.append(summary);
    for (const [key, label] of Object.entries(colors)) {
      const row = document.createElement("div");
      row.className = "theme-color-row";
      const caption = document.createElement("label");
      caption.textContent = label;
      caption.htmlFor = `theme-color-${key}`;
      const swatch = document.createElement("input");
      swatch.type = "color";
      swatch.id = caption.htmlFor;
      swatch.dataset.color = key;
      const hex = document.createElement("input");
      hex.type = "text";
      hex.required = true;
      hex.pattern = "#[0-9a-fA-F]{6}";
      hex.maxLength = 7;
      hex.dataset.hex = key;
      hex.setAttribute("aria-label", `${label} hex color`);
      hex.spellcheck = false;
      const update = (input, other) => {
        if (!/^#[0-9a-f]{6}$/i.test(input.value)) return;
        current().colors[key] = input.value.toLowerCase();
        other.value = input.value;
        status("Unsaved changes.");
        refreshPreview();
      };
      swatch.addEventListener("input", () => update(swatch, hex));
      hex.addEventListener("input", () => update(hex, swatch));
      row.append(caption, swatch, hex);
      group.append(row);
    }
    get("colors").append(group);
  }
  get("sample-link").addEventListener("click", event => event.preventDefault());
  get("name").addEventListener("input", () => { get("name").setCustomValidity(""); current().name = get("name").value; status("Unsaved changes."); });
  get("modes").addEventListener("click", event => {
    const button = event.target.closest("[data-mode]");
    if (!button || !checkFields()) return;
    mode = button.dataset.mode;
    populate();
  });
  get("shared").addEventListener("change", () => {
    if (!checkFields()) { get("shared").checked = draft.shared; return; }
    setShared(draft, get("shared").checked, mode);
    populate();
    status("Unsaved changes. Separate mode palettes are preserved.");
  });
  get("preset").addEventListener("change", () => {
    const [kind, key] = get("preset").value.split(":");
    const theme = kind === "builtin" ? BUILTIN_THEMES[key] : draft.library[Number(key)];
    if (!theme) return;
    draft[draft.shared ? "both" : mode] = copy(theme);
    populate();
    status("Theme loaded into the editor. Save to apply.");
  });
  get("page-preview").addEventListener("change", () => {
    previewPage = get("page-preview").checked;
    if (previewPage) refreshPreview(); else restore();
  });
  get("save").addEventListener("click", () => {
    if (!checkFields()) return;
    try {
      const theme = validateTheme(themeFile(current()));
      draft[draft.shared ? "both" : mode] = theme;
      const index = draft.library.findIndex(saved => saved.name === theme.name);
      if (index < 0) draft.library.push(copy(theme)); else draft.library[index] = copy(theme);
      localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(draft));
      previewPage = false;
      get("page-preview").checked = false;
      restore();
      populate();
      status("Saved in this browser. Saving an existing name updates its library entry.");
    } catch (error) { status(`Could not save: ${error.message}`); }
  });
  get("reset").addEventListener("click", () => {
    draft[draft.shared ? "both" : mode] = copy(BUILTIN_THEMES[mode === "nsfw" && !draft.shared ? "orange" : "blue"]);
    populate();
    status("Default palette restored in editor. Save to apply.");
  });
  get("cancel").addEventListener("click", () => { open(); status("Unsaved edits discarded."); });
  get("export").addEventListener("click", () => {
    if (!checkFields()) return;
    try {
      const theme = validateTheme(themeFile(current()));
      const url = URL.createObjectURL(new Blob([JSON.stringify(themeFile(theme), null, 2) + "\n"], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${theme.name.replace(/[^a-z0-9_-]/gi, "-") || "theme"}.json`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      status("Palette exported. Mode assignments stay in this browser.");
    } catch (error) { status(error.message); }
  });
  get("import").addEventListener("change", async () => {
    const file = get("import").files[0];
    const opened = generation;
    const target = draft.shared ? "both" : mode;
    get("import").value = "";
    if (!file) return;
    try {
      if (file.size > 64000) throw new Error("Palette files must be smaller than 64 KB.");
      const theme = validateTheme(JSON.parse(await file.text()));
      if (opened !== generation) return;
      draft[target] = theme;
      populate();
      status("Imported into editor. Save to keep this theme.");
    } catch (error) { if (opened === generation) status(`Could not import: ${error.message}`); }
  });
  function open() {
    generation++;
    draft = savedThemes();
    mode = isNsfw() ? "nsfw" : "sfw";
    previewPage = false;
    get("page-preview").checked = false;
    restore();
    populate();
    status("");
  }
  return { open, close() { generation++; previewPage = false; restore(); } };
}
